import { Box, Container, Typography, alpha, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, useMediaQuery, useTheme } from '@mui/material';
import { motion } from 'framer-motion';
import { CheckCircleFilled, CloseOutlined } from '@ant-design/icons';
import { SECTION_PY } from 'const/section';

const SystemComparisonSection = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const primaryColor = '#2aa1af';

    // 比較データ：書類作成の連動性と専門的支援の軸に整理
    const rows = [
        /* カテゴリ：基本管理
           請求・勤怠まわりは OHANA AI が意図的に持たない領域。ここは曖昧にせず素直に「×」とし、
           表の下で「請求はやらない／請求以外は丸投げ」というスタンスを言い切る。 */
        { name: '生徒・スタッフ管理', ohana: 'ok', a: 'ok', b: 'ok', c: 'ok', d: 'ok' },
        { name: '請求・勤怠・給与', ohana: 'ng', a: 'ok', b: 'ok', c: 'ng', d: 'ok' },
        /* TODO: 項目名の表現は要調整。
           他社は日報機能はあっても加算書類の作成までは対応していない想定で「△」にしている。 */
        { name: '日報・議事録・加算書類の作成', ohana: 'double', a: 'triangle', b: 'triangle', c: 'triangle', d: 'triangle' },
        { name: 'スケジュール・連絡帳', ohana: 'ng', a: 'ok', b: 'ok', c: 'ok', d: 'ok' },

        /* カテゴリ：療育・診断 */
        { name: 'アセスメント', ohana: 'double', a: 'ng', b: 'ng', c: 'double', d: 'ng' },
        { name: '個別支援計画書', ohana: 'double', a: 'triangle', b: 'ng', c: 'ok', d: 'ng' },
    ];

    const getStatusIcon = (status: string, isOhana: boolean) => {
        const size = isMobile ? '0.9rem' : '1.1rem';
        switch (status) {
            case 'double':
                // OHANA AIは大きく色付き、他社(C社含む)は通常サイズのグレー
                return <CheckCircleFilled style={{ color: isOhana ? primaryColor : '#9ca3af', fontSize: isOhana ? '1.4rem' : size }} />;
            case 'ok':
                return <CheckCircleFilled style={{ color: isOhana ? primaryColor : '#9ca3af', fontSize: size }} />;
            case 'triangle':
                return <Typography component="span" sx={{ fontSize: size, color: '#9ca3af', fontWeight: 900 }}>△</Typography>;
            case 'ng':
                return <CloseOutlined style={{ color: '#d1d5db', fontSize: size }} />;
            default:
                return null;
        }
    };

    const competitors = ['a', 'b', 'c', 'd'] as const;

    return (
        <Box component="section" sx={{ py: SECTION_PY, bgcolor: '#fff', overflow: 'hidden' }}>
            <Container maxWidth="lg">
                <Box sx={{ textAlign: 'center', mb: { xs: 6, md: 8 } }}>
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.8, delay: 0.5 }}
                    >
                        <Typography variant="h2" component="h2" sx={{ fontWeight: 900, fontSize: { xs: '1.8rem', md: '2.5rem' }, mb: 2 }}>
                            システム比較
                        </Typography>
                    </motion.div>
                </Box>

                <motion.div
                    initial={{ opacity: 0, scale: 0.98 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.8, delay: 0.7 }}
                >
                    <TableContainer component={Paper} sx={{
                        boxShadow: `0 10px 40px ${alpha(primaryColor, 0.05)}`,
                        borderRadius: '24px',
                        overflowX: 'auto',
                        border: `1px solid ${alpha(primaryColor, 0.1)}`
                    }}>
                        <Table size="small" sx={{ minWidth: 800 }}>
                            <TableHead sx={{ bgcolor: '#f9fafb' }}>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 900, py: 2, fontSize: '0.8rem', width: '22%' }}>比較項目</TableCell>
                                    <TableCell align="center" sx={{ bgcolor: alpha(primaryColor, 0.1), color: primaryColor, fontWeight: 900, py: 2, fontSize: '0.95rem', borderBottom: `2px solid ${primaryColor}` }}>
                                        OHANA AI
                                    </TableCell>
                                    {competitors.map((comp) => (
                                        <TableCell key={comp} align="center" sx={{ color: '#9ca3af', fontWeight: 800, fontSize: '0.8rem' }}>
                                            {comp.toUpperCase()}社
                                        </TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {rows.map((row, index) => (
                                    <TableRow key={index} sx={{ '&:hover': { bgcolor: '#fdfdfd' } }}>
                                        <TableCell sx={{
                                            fontWeight: index >= 6 ? 900 : 700,
                                            color: index >= 6 ? primaryColor : '#4b5563',
                                            py: 1.5,
                                            fontSize: '0.75rem'
                                        }}>
                                            {row.name}
                                        </TableCell>
                                        <TableCell align="center" sx={{ bgcolor: alpha(primaryColor, 0.02), borderLeft: `1px solid ${alpha(primaryColor, 0.05)}`, borderRight: `1px solid ${alpha(primaryColor, 0.05)}` }}>
                                            {getStatusIcon(row.ohana, true)}
                                        </TableCell>
                                        {competitors.map((comp) => (
                                            <TableCell key={comp} align="center">
                                                {getStatusIcon((row as any)[comp], false)}
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </motion.div>

                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.7, delay: 0.2 }}
                >
                    {/* 請求系の「×」を隠さず、やらないと決めている領域として言い切るブロック */}
                    <Box
                        sx={{
                            mt: { xs: 4, md: 6 },
                            p: { xs: 3, md: 5 },
                            borderRadius: '24px',
                            bgcolor: alpha(primaryColor, 0.05),
                            border: `1px solid ${alpha(primaryColor, 0.15)}`,
                            textAlign: 'center'
                        }}
                    >
                        <Typography
                            component="p"
                            sx={{
                                fontWeight: 900,
                                fontSize: { xs: '1.25rem', md: '2rem' },
                                lineHeight: 1.5,
                                color: '#1f2937'
                            }}
                        >
                            請求業務は、やりません。
                            <Box component="br" />
                            その代わり、
                            <Box component="span" sx={{ color: primaryColor }}>請求以外は OHANA に丸投げ</Box>
                            してください。
                        </Typography>
                    </Box>
                </motion.div>

            </Container>
        </Box>
    );
};

export default SystemComparisonSection;